/**
 * SkuPicker — combobox over the global SKU catalog.
 *
 * The catalog is fetched once and filtered client-side (catalogs run in the
 * thousands of rows, not millions). Filtering matches both code and name
 * case-insensitively.
 *
 * Operator UX:
 * - Type to filter; arrow keys navigate the dropdown; Enter commits.
 * - Esc cancels and reverts the input to the current selection.
 * - Clicking an item commits it.
 *
 * The committed value is the SKU `code` (matches the backend's `class_label`
 * field). The displayed string is "CODE — Name".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createSku, type Sku } from "@/api/skus";
import { useSkuCatalog, lookupSku } from "./sku";
import { colors } from "@/theme/colors";

const MAX_VISIBLE = 50;

type Props = {
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Auto-detected SKU from PDF text. When value is null and confidence is
   *  not "low", a one-click "Aplicar sugerido" chip appears below the input. */
  suggested?: { code: string; confidence: "high" | "medium" | "low" } | null;
};

function formatSku(s: Sku): string {
  return `${s.code} — ${s.name}`;
}

export function SkuPicker({
  value,
  onChange,
  disabled,
  autoFocus,
  suggested,
}: Props) {
  const qc = useQueryClient();
  const { data: skus, isLoading, isError } = useSkuCatalog();
  const selected = useMemo(() => lookupSku(skus, value), [skus, value]);

  const createMut = useMutation({
    mutationFn: (vars: { code: string; name: string }) =>
      createSku(vars.code, vars.name),
    onSuccess: (sku) => {
      qc.invalidateQueries({ queryKey: ["skus"] });
      // Optimistic select: don't wait for the catalog refetch — commit
      // the code right away so the operator can keep going.
      onChange(sku.code);
      setDraft(`${sku.code} — ${sku.name}`);
      setOpen(false);
      setNewName("");
    },
  });
  // Optional name entered in the empty-state inline form.
  const [newName, setNewName] = useState("");

  // The text in the input. Independent from `value` while editing so the
  // operator can type/filter without losing the current selection.
  const [draft, setDraft] = useState<string>(selected ? formatSku(selected) : value ?? "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // When the upstream value changes (cycle navigation, memory prefill), sync
  // the input text. Don't fight the user mid-typing — only update when the
  // dropdown is closed.
  useEffect(() => {
    if (open) return;
    setDraft(selected ? formatSku(selected) : value ?? "");
  }, [open, selected, value]);

  const matches = useMemo(() => {
    if (!skus) return [];
    const needle = draft.trim().toLowerCase();
    if (!needle) return skus.slice(0, MAX_VISIBLE);
    // Pre-filter: substring match on code or name.
    const out: Sku[] = [];
    for (const s of skus) {
      if (
        s.code.toLowerCase().includes(needle) ||
        s.name.toLowerCase().includes(needle)
      ) {
        out.push(s);
        if (out.length >= MAX_VISIBLE) break;
      }
    }
    return out;
  }, [draft, skus]);

  function commit(sku: Sku | null) {
    if (sku) {
      setDraft(formatSku(sku));
      onChange(sku.code);
    } else {
      setDraft("");
      onChange(null);
    }
    setOpen(false);
  }

  function revertDraft() {
    setDraft(selected ? formatSku(selected) : value ?? "");
    setOpen(false);
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        value={draft}
        autoFocus={autoFocus}
        disabled={disabled || isLoading}
        placeholder={
          isError
            ? "(falha ao carregar catálogo)"
            : isLoading
              ? "carregando catálogo..."
              : "busque por código ou nome"
        }
        onFocus={(e) => {
          // Select all on focus so the operator can replace an existing
          // selection by just typing — no need to backspace through
          // "CODE — Name" first.
          e.currentTarget.select();
          setOpen(true);
        }}
        onBlur={() => {
          // Defer so a click on a dropdown item still registers.
          setTimeout(() => setOpen(false), 120);
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const pick = matches[highlight];
            if (pick) commit(pick);
          } else if (e.key === "Escape") {
            e.preventDefault();
            revertDraft();
            (e.target as HTMLInputElement).blur();
          }
        }}
        style={{
          padding: "8px 10px",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-body)",
          border: `1px solid ${colors.line}`,
          borderRadius: "var(--radius-sm)",
          background: colors.panel,
          color: colors.text,
          width: "100%",
          boxSizing: "border-box",
        }}
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 20,
            margin: 0,
            padding: 0,
            listStyle: "none",
            background: colors.panel,
            border: `1px solid ${colors.line}`,
            borderRadius: "var(--radius-sm)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {matches.map((s, i) => (
            <li
              key={s.code}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault(); // keep input focus
                commit(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "6px 10px",
                cursor: "pointer",
                background: i === highlight ? colors.panelMuted : "transparent",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-ui)",
                color: colors.text,
                display: "flex",
                gap: 8,
              }}
            >
              <span style={{ color: colors.textMuted, minWidth: 110 }}>{s.code}</span>
              <span>{s.name}</span>
            </li>
          ))}
        </ul>
      )}
      {!value && suggested && suggested.confidence !== "low" && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onChange(suggested.code);
          }}
          disabled={disabled}
          style={{
            marginTop: 4,
            padding: "4px 8px",
            background: "transparent",
            border: `1px dashed ${colors.intelligence}`,
            borderRadius: "var(--radius-sm)",
            color: colors.intelligence,
            cursor: "pointer",
            fontSize: "var(--fs-ui)",
            textAlign: "left",
            fontFamily: "var(--font-mono)",
          }}
          title={`Sugerido pelo PDF (confiança: ${suggested.confidence})`}
        >
          Sugerido: {suggested.code}{" "}
          <span style={{ color: colors.textMuted }}>
            ({suggested.confidence})
          </span>
        </button>
      )}
      {open && !isLoading && matches.length === 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 20,
            padding: "6px 10px",
            background: colors.panel,
            border: `1px solid ${colors.line}`,
            borderRadius: "var(--radius-sm)",
            fontSize: "var(--fs-ui)",
            color: colors.textMuted,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span style={{ fontStyle: "italic" }}>
            Sem resultados — criar novo SKU?
          </span>
          {draft.trim() && (
            <>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    createMut.mutate({
                      code: draft.trim(),
                      name: newName.trim(),
                    });
                  }
                }}
                placeholder="nome (opcional)"
                style={{
                  padding: "4px 8px",
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-ui)",
                  border: `1px solid ${colors.line}`,
                  borderRadius: "var(--radius-sm)",
                  background: colors.canvas,
                  color: colors.text,
                }}
              />
              <button
                type="button"
                onMouseDown={(e) => {
                  // Keep input focus so the dropdown doesn't blur away
                  // before the click registers.
                  e.preventDefault();
                  createMut.mutate({
                    code: draft.trim(),
                    name: newName.trim(),
                  });
                }}
                disabled={createMut.isPending}
                style={{
                  padding: "6px 10px",
                  background: colors.primary,
                  color: colors.onPrimary,
                  border: "none",
                  borderRadius: "var(--radius-sm)",
                  cursor: "pointer",
                  fontSize: "var(--fs-ui)",
                  fontWeight: 600,
                }}
              >
                {createMut.isPending
                  ? "Criando…"
                  : `Criar "${draft.trim()}"${newName.trim() ? ` — ${newName.trim()}` : ""}`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
